/*  ========================================================================
    # Section - Team Opinions
    ========================================================================  */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import bem from '../../utils/bem';
import { theme } from '../../../../tailwind.config';
import { useSwipeable } from 'react-swipeable';

// Hooks
import useLocalization from '../../hooks/useLocalization';

// Components
import LazyImage from '../../components/LazyImage';

const TeamOpinions = ({ heading, opinions }) => {
  const wrapperRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(3);
  const local = useLocalization();
  const breakpoints = theme.screens;

  const canScrollLeft = activeSlide > 0;
  const canScrollRight = activeSlide + slideCount < opinions.length;

  const handleScrollRight = () => {
    if (canScrollRight) {
      setActiveSlide(prev => prev + 1);
    }
  };

  const handleScrollLeft = () => {
    if (canScrollLeft) {
      setActiveSlide(prev => prev - 1);
    }
  };

  const isActiveSlide = index => {
    return index >= activeSlide && index + 1 <= slideCount + activeSlide;
  };


  const centerElements = () => {
    if (window.innerWidth > parseFloat(breakpoints.xxl)) {
      // width is 33% so less than 3 elements need to be centered
      if (opinions.length < 3)
        return true;
    } else if (window.innerWidth > parseFloat(breakpoints.lg)) {
      // width is 50% so if there is only 1 item it needs to be centered
      if (opinions.length < 2)
        return true;
    }

    return false;
  }

  useEffect(() => {
    const sliderObserver = new ResizeObserver(entries => {
      const targetWidth = entries[0].target.offsetWidth;

      if (targetWidth >= parseFloat(breakpoints.xl)) {
        setSlideCount(3);
      } else if (targetWidth >= parseFloat(breakpoints.md)) {
        setSlideCount(2);
      } else {
        setSlideCount(1);
      }
    });

    sliderObserver.observe(wrapperRef.current);

    return () => sliderObserver.unobserve(wrapperRef.current);
  }, []);

  // Create handlers for swipe recognition.
  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleScrollRight,
    onSwipedRight: handleScrollLeft
  });

  return (
    <>
      {heading ? (
        <div
          className={`s-team-opinions__heading wysiwyg-block`}
          dangerouslySetInnerHTML={{ __html: heading }}
        />
      ) : null}
      <div className={`s-team-opinions__controls`}>
        <button
          type='button'
          className={`s-team-opinions__control-left`}
          onClick={() => handleScrollLeft()}
          disabled={!canScrollLeft}
        >
          <svg viewBox='0 0 200 200'>
            <use href={`${local('svgSprite')}#chevron-left`} />
          </svg>
        </button>
        <button
          type='button'
          className={`s-team-opinions__control-right`}
          onClick={() => handleScrollRight()}
          disabled={!canScrollRight}
        >
          <svg viewBox='0 0 200 200'>
            <use href={`${local('svgSprite')}#chevron-right`} />
          </svg>
        </button>
      </div>
      <div className={`s-team-opinions__wrapper`} ref={wrapperRef}>
        <div className={bem('s-team-opinions', 'slider', { 'center': centerElements() })} style={{ '--slide-offset': activeSlide }} {...swipeHandlers}>
          {opinions.map((opinion, index) => (
            <div
              className={bem('s-team-opinions', 'slide', { active: isActiveSlide(index) })}
              key={index}
            >
              <LazyImage
                figureClass={'s-team-opinions__slide-image'}
                defaultUrl={opinion.image.sizes.medium}
                placeholderUrl={opinion.image.sizes.placeholder}
                alt={opinion.image.alt}
              />
              <div className={`s-team-opinions__slide-content`}>
                <p className={`s-team-opinions__slide-name`}>{opinion.name}</p>
                <p className={`s-team-opinions__slide-position`}>{opinion.position}</p>
                {opinion.comment ? (
                  <p className={`s-team-opinions__slide-comment`}>{opinion.comment}</p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

TeamOpinions.propTypes = {
  heading: PropTypes.string,
  opinions: PropTypes.array
};

export default TeamOpinions;
